import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { AlertCircle, X } from "lucide-react";

interface Toast {
  id: string;
  message: string;
  type: "error";
}

interface ToastContextType {
  showError: (message: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback(
    (message: string, type: Toast["type"]) => {
      const id = Math.random().toString(36).substr(2, 9);
      const toast: Toast = { id, message, type };

      setToasts((prev) => [...prev, toast]);

      setTimeout(() => {
        removeToast(id);
      }, 5000);
    },
    [removeToast],
  );

  const showError = useCallback(
    (message: string) => {
      addToast(message, "error");
    },
    [addToast],
  );

  const getToastStyles = (type: Toast["type"]) => {
    switch (type) {
      case "error":
        return "bg-red-50 border-red-200 text-red-800";
      default:
        return "bg-gray-50 border-gray-200 text-gray-800";
    }
  };

  const getIconColor = (type: Toast["type"]) => {
    switch (type) {
      case "error":
        return "text-red-500";
      default:
        return "text-gray-500";
    }
  };

  return (
    <ToastContext.Provider value={{ showError }}>
      {children}

      <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`
              ${getToastStyles(toast.type)}
              border rounded-lg shadow-lg p-4 transform transition-all duration-300 ease-in-out
              animate-in slide-in-from-right fade-in
            `}
          >
            <div className="flex items-start gap-3">
              <AlertCircle
                className={`h-5 w-5 ${getIconColor(toast.type)} flex-shrink-0 mt-0.5`}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium break-words">
                  {toast.message}
                </p>
              </div>
              <button
                onClick={() => removeToast(toast.id)}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
